import { useState, useEffect } from "react";
import { Shield, Bell, Palette, Lock, Eye, EyeOff } from "lucide-react";
import Navbar from "../components/navigation/Navbar";
import * as allauth from "../library/allauth";
import { useNavigate } from "react-router-dom";

const NAV_ITEMS = [
  { key: "security", label: "Security", icon: Shield, enabled: true },
  { key: "notifications", label: "Notifications", icon: Bell, enabled: true },
  { key: "appearance", label: "Appearance", icon: Palette, enabled: false },
  { key: "privacy", label: "Privacy", icon: Lock, enabled: false },
];

export default function Settings() {
  const [activeTab, setActiveTab] = useState("security");

  return (
    <>
      <Navbar />
      <div className="min-h-screen bg-gray-50 pt-14">
        <div className="max-w-6xl mx-auto px-4 py-8">
          <div className="mb-6">
            <h1 className="text-xl font-semibold text-gray-900">Settings</h1>
            <p className="text-sm text-gray-400 mt-0.5">
              Manage your account security and preferences
            </p>
          </div>

          <div className="flex gap-6">
            {/* Left Sidebar Nav */}
            <div className="w-48 flex-shrink-0">
              <div className="bg-white rounded-xl border border-gray-200 p-2">
                {NAV_ITEMS.map((item) => {
                  const Icon = item.icon;
                  return (
                    <button
                      key={item.key}
                      onClick={() => item.enabled && setActiveTab(item.key)}
                      disabled={!item.enabled}
                      className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors text-left mb-0.5 ${
                        activeTab === item.key
                          ? "bg-emerald-50 text-emerald-700"
                          : item.enabled
                            ? "text-gray-600 hover:bg-gray-50"
                            : "text-gray-300 cursor-not-allowed"
                      }`}
                    >
                      <Icon size={16} />
                      <span>{item.label}</span>
                      {!item.enabled && (
                        <span className="ml-auto text-xs text-gray-300">
                          soon
                        </span>
                      )}
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Right Content */}
            <div className="flex-1 space-y-4">
              {activeTab === "security" && <SecurityTab />}
              {activeTab === "notifications" && <NotificationsTab />}
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
function MFASection() {
  const navigate = useNavigate();
  const [mfaEnabled, setMfaEnabled] = useState(null); // null = loading
  const [disabling, setDisabling] = useState(false);

  useEffect(() => {
    allauth.getTOTPAuthenticator().then((res) => {
      setMfaEnabled(res.status === 200);
    });
  }, []);

  const handleDisable = async () => {
    if (
      !window.confirm(
        "Disable two-factor authentication? Your account will be less secure.",
      )
    )
      return;
    setDisabling(true);
    try {
      const res = await allauth.deactivateTOTP();
      if (res.status === 200) {
        setMfaEnabled(false);
      }
    } finally {
      setDisabling(false);
    }
  };

  if (mfaEnabled === null) {
    return <p className="text-sm text-gray-400">Loading...</p>;
  }

  if (mfaEnabled) {
    return (
      <div className="space-y-4">
        <div className="flex items-center gap-3 p-4 bg-emerald-50 rounded-lg border border-emerald-200">
          <span className="w-2.5 h-2.5 rounded-full bg-emerald-500 flex-shrink-0" />
          <div>
            <p className="text-sm font-medium text-emerald-800">
              Two-factor authentication is active
            </p>
            <p className="text-xs text-emerald-600 mt-0.5">
              Your account is protected with TOTP
            </p>
          </div>
        </div>
        <div className="flex gap-3">
          <button
            onClick={() => navigate("/mfa-setup")}
            className="px-4 py-2 text-sm text-gray-600 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
          >
            Manage 2FA
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="bg-gray-50 rounded-lg p-4">
        <p className="text-sm font-medium text-gray-700 mb-1">
          Protect your account with TOTP
        </p>
        <p className="text-xs text-gray-400 mb-3 leading-relaxed">
          Works with Google Authenticator, Microsoft Authenticator, Authy and
          1Password.
        </p>
        <button
          onClick={() => navigate("/mfa-setup")}
          className="px-4 py-2 text-sm font-medium text-white bg-emerald-700 rounded-lg hover:bg-emerald-800 transition-colors"
        >
          Set up authenticator app
        </button>
      </div>
      <div className="flex items-center gap-2">
        <span className="w-2 h-2 rounded-full bg-red-400" />
        <p className="text-xs text-red-500">
          Not enabled — your account is less secure
        </p>
      </div>
    </div>
  );
}
function SecurityTab() {
  const [showPasswordForm, setShowPasswordForm] = useState(false);
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showCurrent, setShowCurrent] = useState(false);
  const [showNew, setShowNew] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [passwordError, setPasswordError] = useState("");
  const [passwordSuccess, setPasswordSuccess] = useState("");
  const [saving, setSaving] = useState(false);
  const navigate = useNavigate();

  const handleChangePassword = async () => {
    setPasswordError("");
    setPasswordSuccess("");
    if (!currentPassword) {
      setPasswordError("Current password is required.");
      return;
    }
    if (!newPassword) {
      setPasswordError("New password is required.");
      return;
    }
    if (newPassword.length < 8) {
      setPasswordError("Password must be at least 8 characters.");
      return;
    }
    if (newPassword !== confirmPassword) {
      setPasswordError("Passwords do not match.");
      return;
    }
    setSaving(true);
    try {
      const data = await allauth.changePassword({
        current_password: currentPassword,
        new_password: newPassword,
      });
      if (data.status === 200) {
        setPasswordSuccess("Password changed successfully.");
        setCurrentPassword("");
        setNewPassword("");
        setConfirmPassword("");
        setShowPasswordForm(false);
        setTimeout(() => {
          window.location.href = "/login";
        }, 1500);
      } else {
        setPasswordError(
          data.errors?.[0]?.message || "Failed to change password.",
        );
      }
    } catch {
      setPasswordError("Something went wrong. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      {/* Password */}
      <div className="bg-white rounded-xl border border-gray-200 p-6">
        <div className="flex items-center gap-2 mb-5">
          <Lock size={15} className="text-purple-500" />
          <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider">
            Password
          </p>
        </div>

        {passwordSuccess && (
          <div className="mb-4 px-3 py-2 bg-green-50 border border-green-200 rounded-lg text-sm text-green-700">
            {passwordSuccess}
          </div>
        )}

        {!showPasswordForm ? (
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-700">
                Change password
              </p>
              <p className="text-xs text-gray-400 mt-0.5">
                Update your account password
              </p>
            </div>
            <button
              onClick={() => setShowPasswordForm(true)}
              className="px-4 py-2 text-sm text-gray-600 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
            >
              Change password
            </button>
          </div>
        ) : (
          <div className="space-y-3">
            <div>
              <label className="block text-xs text-gray-500 mb-1">
                Current password
              </label>
              <div className="relative">
                <Lock
                  size={14}
                  className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"
                />
                <input
                  type={showCurrent ? "text" : "password"}
                  value={currentPassword}
                  onChange={(e) => setCurrentPassword(e.target.value)}
                  className="w-full pl-9 pr-10 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500"
                  placeholder="Enter current password"
                />
                <button
                  type="button"
                  onClick={() => setShowCurrent(!showCurrent)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                >
                  {showCurrent ? <EyeOff size={15} /> : <Eye size={15} />}
                </button>
              </div>
            </div>
            <div>
              <label className="block text-xs text-gray-500 mb-1">
                New password
              </label>
              <div className="relative">
                <Lock
                  size={14}
                  className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"
                />
                <input
                  type={showNew ? "text" : "password"}
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  className="w-full pl-9 pr-10 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500"
                  placeholder="At least 8 characters"
                />
                <button
                  type="button"
                  onClick={() => setShowNew(!showNew)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                >
                  {showNew ? <EyeOff size={15} /> : <Eye size={15} />}
                </button>
              </div>
            </div>
            <div>
              <label className="block text-xs text-gray-500 mb-1">
                Confirm new password
              </label>
              <div className="relative">
                <Lock
                  size={14}
                  className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"
                />
                <input
                  type={showConfirm ? "text" : "password"}
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  className="w-full pl-9 pr-10 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500"
                  placeholder="Repeat new password"
                />
                <button
                  type="button"
                  onClick={() => setShowConfirm(!showConfirm)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                >
                  {showConfirm ? <EyeOff size={15} /> : <Eye size={15} />}
                </button>
              </div>
            </div>
            {passwordError && (
              <p className="text-sm text-red-500">{passwordError}</p>
            )}
            <div className="flex gap-3 pt-1">
              <button
                onClick={() => {
                  setShowPasswordForm(false);
                  setPasswordError("");
                  setCurrentPassword("");
                  setNewPassword("");
                  setConfirmPassword("");
                }}
                className="flex-1 py-2 text-sm text-gray-600 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleChangePassword}
                disabled={saving}
                className="flex-1 py-2 text-sm font-medium text-white bg-emerald-700 rounded-lg hover:bg-emerald-800 transition-colors disabled:opacity-60"
              >
                {saving ? "Saving..." : "Update password"}
              </button>
            </div>
          </div>
        )}
      </div>

      {/* MFA */}
      <div className="bg-white rounded-xl border border-gray-200 p-6">
        <div className="flex items-center gap-2 mb-5">
          <Shield size={15} className="text-purple-500" />
          <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider">
            Two-factor authentication
          </p>
        </div>
        <MFASection />
      </div>

      {/* Active Sessions */}
      <div className="bg-white rounded-xl border border-gray-200 p-6">
        <div className="flex items-center gap-2 mb-5">
          <Shield size={15} className="text-purple-500" />
          <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider">
            Active sessions
          </p>
        </div>
        <div className="space-y-3">
          <div className="flex items-center justify-between py-2 border-b border-gray-100">
            <div>
              <p className="text-sm font-medium text-gray-700">
                Current session
              </p>
              <p className="text-xs text-gray-400 mt-0.5">
                This device — active now
              </p>
            </div>
            <span className="px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-700">
              Current
            </span>
          </div>
        </div>
      </div>
    </>
  );
}

function NotificationsTab() {
  const [prefs, setPrefs] = useState({
    permission_changes: true,
    login_alerts: false,
    mfa_failed: true,
  });

  const toggle = (key) => setPrefs((p) => ({ ...p, [key]: !p[key] }));

  const items = [
    {
      key: "permission_changes",
      label: "Permission change alerts",
      sub: "Email when your access or role changes",
    },
    {
      key: "login_alerts",
      label: "New login alert",
      sub: "Email when someone logs into your account",
    },
    {
      key: "mfa_failed",
      label: "MFA challenge failed",
      sub: "Alert on failed authentication attempts",
    },
  ];

  return (
    <div className="bg-white rounded-xl border border-gray-200 p-6">
      <div className="flex items-center gap-2 mb-5">
        <Bell size={15} className="text-amber-500" />
        <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider">
          Notification preferences
        </p>
      </div>
      <div className="space-y-1">
        {items.map((item) => (
          <div
            key={item.key}
            className="flex items-center justify-between py-3 border-b border-gray-100 last:border-0"
          >
            <div>
              <p className="text-sm font-medium text-gray-700">{item.label}</p>
              <p className="text-xs text-gray-400 mt-0.5">{item.sub}</p>
            </div>
            <button
              onClick={() => toggle(item.key)}
              className={`relative w-10 h-5 rounded-full transition-colors ${prefs[item.key] ? "bg-emerald-600" : "bg-gray-200"}`}
            >
              <div
                className={`absolute top-0.5 left-0.5 w-4 h-4 bg-white rounded-full shadow transition-transform ${prefs[item.key] ? "translate-x-5" : "translate-x-0"}`}
              />
            </button>
          </div>
        ))}
      </div>
      <p className="text-xs text-gray-400 mt-4">
        Notification delivery coming soon — toggles are saved locally for now
      </p>
    </div>
  );
}
